import React, { useEffect, useRef, useState } from "react"
import Value, { setValue } from "aldoo-ra/Value"
import Button from "aldoo-ra/Button"
import { LockClosedIcon } from "@heroicons/react/24/solid"
import PaymentLogos from "../../checkout/PaymentLogos"
import { useCartPaymentMethods } from "./paymentMethods"
import Spinner from "aldoo-ra/Spinner"
import useT from "../../hooks/useT"
import PriceDisplay from "../PriceDisplay"
import { formatMoney } from "../../account/ui"
import { cartSubtotal } from "../../checkout/cartItems"
import { useCartMinimums } from "./minimums"
import { CART_VALIDATION_KEY, scheduleStockCheck } from "../../store/stockCheck"
import { useCurrency } from "../../store/currency"

/**
 * Cart footer (fragment "shopping-cart-footer"): the total and Checkout. The
 * delivery is not in it: its price depends on the way the buyer picks
 * (address, office, locker) in the checkout's delivery step. The lines are
 * checked against the stock when the cart opens and as it changes
 * (store/stockCheck); invalid ones can be removed in one click and block the
 * checkout meanwhile.
 */
export default function CartFooter({ store }) {
  const t = useT()
  // the ways this cart can be paid (its sellers' payment methods)
  const payment = useCartPaymentMethods(store.cart || [])
  const [promoInfo] = Value({ key: "aldoo-promo-code-info", defaultValue: null, persistKey: "aldoo-promo-code-info" })
  // the promo field, opened from "Have a promo code?"
  const [promoOpen, setPromoOpen] = useState(false)
  const clearPromo = () => {
    store.resetPromoCode?.()
    store.setPromoCodeError?.(null)
    setPromoOpen(false)
  }
  const lines = store.cart
  // a seller's minimum order not reached: the checkout waits (the seller's line in the cart says how much more)
  const short = (useCartMinimums(store.cart || []) || []).filter((entry) => !entry.met)
  // everything is shown in the cart's currency, whatever each product is
  // priced in (CartTitle picks it, the API charges in it)
  const { currency, convert } = useCurrency()
  const subtotal = cartSubtotal(lines, convert)
  // the lines checked against the stock when the cart opens and whenever it
  // changes — one debounced request for the whole cart (store/stockCheck)
  const [validation] = Value(CART_VALIDATION_KEY, null)
  const signature = JSON.stringify(lines.map((l) => [l.pid, l.quantity, l.options?.platform, l.lineKey]))
  const asked = useRef(null)
  useEffect(() => {
    asked.current = signature
    scheduleStockCheck()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])
  // an answer is the cart's only while it is the cart it was asked for
  const validating = !validation || (validation.items || []).length !== lines.length

  const removeInvalid = () => validation?.items.filter((i) => !i.valid).forEach((i) => store.removeItem(i.key))

  // Stripe promotion code on the cart (framework store: applyPromoCode →
  // store {get_promo_code_info}). The discount shown here is indicative — the
  // API resolves the code again and takes it off the amount it charges.
  // the framework keeps the resolved code in a shared Value; getPromoCodeInfo()
  // only formats it for display (" -20%")
  const applied = promoInfo?.promoCodeId && store.promoCode && !store.promoCodeError ? promoInfo : null
  const discountCents = (() => {
    if (!applied) return 0
    if (applied.minimumAmount && subtotal < applied.minimumAmount) return 0
    if (applied.type === "percentage") return Math.round(subtotal * (applied.amount / 100))
    if (applied.type === "fixed") return Math.min(subtotal, Math.round(applied.amount))
    return 0
  })()
  const discountLabel = applied?.type === "percentage" ? `-${applied.amount}%` : discountCents ? `-${formatMoney(discountCents, currency)}` : ""

  return (
    <div className="shrink-0 border-t border-bordered px-5 py-4 flex flex-col gap-3 bg-paper" data-testid="cart-footer">
      {validation && !validation.valid && (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-error/10 px-4 py-3 text-[14px] text-error" data-testid="cart-validation">
          <span>{validation.invalidCount === 1 ? t`1 item in your cart is no longer available` : t("%count% items in your cart are no longer available", validation.invalidCount)}</span>
          <button type="button" onClick={removeInvalid} className="font-bold underline whitespace-nowrap">
            {t`Remove Invalid Items`}
          </button>
        </div>
      )}
      {/* the code goes in before the sums, so the recap under it is the answer: what the code took
          off is a line of that recap. Most orders have no code: the field waits behind "Have a promo
          code?" beside the total, and stays open once something is typed (or refused) */}
      {!applied && (promoOpen || !!store.promoCode || !!store.promoCodeError) && <PromoCode store={store} autoFocus={promoOpen} />}
      {/* what the code takes off, in bold: the total under it is already the price after it */}
      {discountCents > 0 && (
        <div className="flex items-center justify-between text-[15px] font-bold text-text" data-testid="cart-discount">
          <span>
            {t`Discount`} · <span className="uppercase">{store.promoCode}</span>
          </span>
          <span>{discountLabel}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        {applied ? (
          <Button variant="link" onClick={clearPromo} className="text-[14px] text-left" data-testid="cart-promo-clear">
            {t`Remove the promo code`}
          </Button>
        ) : !(promoOpen || store.promoCode || store.promoCodeError) ? (
          <Button variant="link" onClick={() => setPromoOpen(true)} className="text-[14px] text-left" data-testid="cart-promo-open">
            {t`Have a promo code?`}
          </Button>
        ) : (
          <span />
        )}
        {/* the total with its label beside it */}
        <span className="flex items-center gap-2">
          <span className="text-[16px] font-semibold">{t`Total`}:</span>
          <PriceDisplay amount={Math.max(0, subtotal - discountCents)} currency={currency} variant="medium" className="min-w-0" />
        </span>
      </div>
      {short.length > 0 && (
        <p className="m-0 text-center text-[13px] text-error" data-testid="cart-min-order-block">
          {short.length === 1 ? t("Add %s more from %s to reach its minimum order.", formatMoney(short[0].remaining, short[0].currency), short[0].sellerName) : t`Some sellers' minimum orders are not reached yet.`}
        </p>
      )}
      <Button variant="primary_l" className="w-full" disabled={lines.length === 0 || validating || (validation && !validation.valid) || short.length > 0} data-testid="checkout-button" onClick={() => store.beginCheckout()}>
        {t`Proceed to Checkout`}
      </Button>
      {/* who takes the card, said where the card is about to be handed over, and the ways it takes
          (the same marks as the payment step: checkout/PaymentLogos) */}
      {/* only the ways this cart can be paid: Stripe's line goes with the online marks */}
      <div className="flex flex-col items-center gap-2" data-testid="secure-payments">
        {payment.online && (
          <span className="flex items-center gap-1.5 text-[13px] text-text_secondary">
            <LockClosedIcon className="w-4 h-4" />
            {t`Secure Payments by Stripe`}
          </span>
        )}
        <PaymentLogos online={payment.online} cashOnDelivery={payment.cashOnDelivery} />
      </div>
    </div>
  )
}

/** The promo code field and Apply (opened from "Have a promo code?" beside the total). */
function PromoCode({ store, autoFocus = false }) {
  const t = useT()
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)

  const apply = async () => {
    if (!store.promoCode || busy) return
    setBusy(true)
    try {
      await store.applyPromoCode()
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {/* styled like the "aldoo" Input variant (Variants/Input.jsx) */}
        <input
          ref={inputRef}
          value={store.promoCode || ""}
          onChange={(e) => {
            store.setPromoCodeError?.(null)
            store.setPromoCode(e.target.value.toUpperCase())
          }}
          onKeyDown={(e) => e.key === "Enter" && apply()}
          placeholder={t`Enter a promo code`}
          className="h-[40px] flex-1 min-w-0 rounded text-sm font-medium text-text placeholder:text-text_secondary placeholder:font-normal bg-paper border border-bordered focus:outline-none focus:ring-0 focus:border-text px-4"
          data-testid="cart-promo-input"
        />
        <Button
          variant="secondary"
          onClick={apply}
          disabled={busy || !store.promoCode}
          //className="h-[44px] px-6 rounded-full border border-primary text-primary text-[15px] font-semibold hover:bg-primary hover:text-text_inv transition-colors disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-primary"
          data-testid="cart-promo-apply"
        >
          {busy ? <Spinner variant="primary" size="sm" className="mx-auto" /> : t`Apply`}
        </Button>
      </div>
      {store.promoCodeError && (
        <span className="text-[13px] text-error" data-testid="cart-promo-error">
          {store.promoCodeError === "promo_code_not_found" ? t`This promo code is not valid` : t`This promo code cannot be used`}
        </span>
      )}
    </div>
  )
}
